import React, { useEffect, useMemo, useState } from 'react';
import { Form, Grid, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import BankCodeDropdown from '../../../components/BankCodeDropdown';
import { dangerNotification, successNotification } from '../../../services/notification';
import {
  updateCommissionCollaboratorBankAccount,
  updateStandaloneChargeEstateBankAccount
} from '../../../services/standalone-charges';
import { ActivationFormState } from '../types';
import { initialActivationForm } from '../constants';
import {
  normalizePagarmeBankHolderName,
  PAGARME_BANK_HOLDER_NAME_MAX_LENGTH
} from '../utils/activationFormHelpers';
import { buildPagarmeActivationPayload } from '../utils/buildPagarmeActivationPayload';
import { mapActivationPayloadToForm } from '../utils/mapActivationPayloadToForm';
import { validateActivationStep } from '../utils/validateActivationStep';

type BankAccountSource = {
  recipientType?: ActivationFormState['recipientType'] | string;
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
  payload?: Record<string, unknown>;
};

type Props = {
  open: boolean;
  mode: 'estate' | 'collaborator';
  estateId?: string;
  collaboratorId?: string;
  source: BankAccountSource | null;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

const EditBankAccountModal: React.FC<Props> = ({
  open,
  mode,
  estateId,
  collaboratorId,
  source,
  onClose,
  onSuccess
}) => {
  const [form, setForm] = useState<ActivationFormState>(initialActivationForm);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    const mapped = mapActivationPayloadToForm(
      {
        ...source,
        recipientType:
          source.recipientType === 'individual' ||
            source.recipientType === 'corporation'
            ? source.recipientType
            : undefined
      },
      initialActivationForm
    );
    setForm(mapped);
    setError(null);
  }, [open, source]);

  const documentReadOnly = useMemo(
    () => form.bankHolderDocument || form.document,
    [form.bankHolderDocument, form.document]
  );

  const updateField = <K extends keyof ActivationFormState>(
    field: K,
    value: ActivationFormState[K]
  ) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'bankHolderName') {
        next.bankHolderName = normalizePagarmeBankHolderName(String(value || ''));
      }
      return next;
    });
  };

  const handleSave = async () => {
    const validationError = validateActivationStep(5, form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildPagarmeActivationPayload(form);
      if (mode === 'estate') {
        await updateStandaloneChargeEstateBankAccount({
          ...(estateId ? { estateId } : {}),
          defaultBankAccount: payload.defaultBankAccount
        });
      } else {
        if (!collaboratorId) {
          throw new Error('Colaborador não informado.');
        }
        await updateCommissionCollaboratorBankAccount(collaboratorId, {
          defaultBankAccount: payload.defaultBankAccount
        });
      }

      successNotification(
        'Sucesso',
        'Conta bancária de recebimento atualizada com sucesso.'
      );
      await onSuccess?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(message || 'Não foi possível atualizar a conta bancária.');
      dangerNotification(
        'Erro',
        message || 'Não foi possível atualizar a conta bancária.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>Alterar conta bancária</Modal.Header>
      <Modal.Content>
        <Message info>
          <p style={{ margin: 0 }}>
            Altera apenas a conta de recebimento Pagar.me. Não será necessário
            refazer a biometria.
          </p>
        </Message>
        {error && <Message negative>{error}</Message>}
        <Form>
          <Grid>
            <Grid.Row columns={3}>
              <Grid.Column>
                <Form.Field>
                  <label>Nome do titular</label>
                  <Input
                    maxLength={PAGARME_BANK_HOLDER_NAME_MAX_LENGTH}
                    value={form.bankHolderName}
                    onChange={(_, { value }) => updateField('bankHolderName', value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Tipo do titular</label>
                  <Input
                    value={
                      form.bankHolderType === 'company'
                        ? 'Pessoa Jurídica'
                        : 'Pessoa Física'
                    }
                    readOnly
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Documento do titular</label>
                  <Input value={documentReadOnly} readOnly />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={5}>
              <Grid.Column>
                <Form.Field>
                  <label>Código do Banco</label>
                  <BankCodeDropdown
                    fluid
                    value={form.bankCode}
                    onChange={(_, { value }) =>
                      updateField('bankCode', String(value || ''))
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Agência</label>
                  <Input
                    value={form.bankBranchNumber}
                    onChange={(_, { value }) =>
                      updateField('bankBranchNumber', value.replace(/\D/g, ''))
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Dígito agência (opcional)</label>
                  <Input
                    value={form.bankBranchCheckDigit}
                    onChange={(_, { value }) =>
                      updateField('bankBranchCheckDigit', value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Conta</label>
                  <Input
                    value={form.bankAccountNumber}
                    onChange={(_, { value }) =>
                      updateField(
                        'bankAccountNumber',
                        value.replace(/\D/g, '').slice(0, 13)
                      )
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Dígito conta</label>
                  <Input
                    value={form.bankAccountCheckDigit}
                    onChange={(_, { value }) =>
                      updateField('bankAccountCheckDigit', value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" light onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          type="button"
          text="Salvar conta bancária"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default EditBankAccountModal;
